import { useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import LeadDialog from "./LeadDialog";
import MobileCaptureDialog from "./MobileCaptureDialog";
import ShareButton from "./ShareButton";
import { doTrackedRedirect } from "../lib/track";
import { Link } from "react-router-dom";

export default function ProductCard({ product, variant = "default" }) {
  const [openLead, setOpenLead] = useState(false);
  const [openMobile, setOpenMobile] = useState(false);
  const isBanking = product.type === "banking";

  // Actually generate the tracked URL + open it. Called both from the plain
  // path and again after the mobile-capture modal returns for banking.
  const doRedirect = () => doTrackedRedirect(product);

  const handleCta = async (e) => {
    e.preventDefault();
    // Banking products ALWAYS show the mobile-capture dialog on every click
    // — the lender needs a fresh phone per lead attempt for eligibility.
    if (isBanking) {
      setOpenMobile(true);
      return;
    }
    if (product.lead_mode === "redirect_only") {
      await doRedirect();
      return;
    }
    // form_only or form_then_redirect -> open lead form dialog
    setOpenLead(true);
  };

  return (
    <>
      <Card
        data-testid={`product-card-${product.id}`}
        className="group overflow-hidden border border-black/8 shadow-none hover:shadow-lg transition-shadow duration-300 bg-white flex flex-col"
      >
        <div className="relative aspect-[16/10] overflow-hidden bg-white flex items-center justify-center">
          {product.image_url && (
            <img
              src={product.image_url}
              alt={product.title}
              loading="lazy"
              className={
                isBanking
                  ? "max-w-[70%] max-h-[70%] object-contain group-hover:scale-[1.03] transition-transform duration-500"
                  : "max-w-[60%] max-h-[60%] object-contain group-hover:scale-[1.03] transition-transform duration-500"
              }
              style={{ imageRendering: "auto" }}
            />
          )}
          <div className="absolute top-3 left-3 flex gap-2">
            <Badge className="badge-mono bg-white/95 text-black border border-black/10 hover:bg-white">
              {isBanking ? product.category.replace("_", " ") : product.category}
            </Badge>
          </div>
          {!isBanking && product.original_price && product.price && (
            <div className="absolute top-3 right-3 bg-[#0055FF] text-white text-xs px-2 py-1 rounded-full font-medium">
              -{Math.round((1 - product.price / product.original_price) * 100)}%
            </div>
          )}
        </div>

        <div className="p-5 flex-1 flex flex-col">
          <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-1">{product.provider}</div>
          <Link to={`/product/${product.id}`} className="font-display text-lg leading-snug font-semibold tracking-tight hover:underline underline-offset-4">
            {product.title}
          </Link>
          {product.subtitle && (
            <p className="text-sm text-neutral-600 mt-1.5">{product.subtitle}</p>
          )}

          {isBanking ? (
            <ul className="mt-4 space-y-1.5">
              {(product.features || []).slice(0, 3).map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-neutral-700">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#00A36C] flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          ) : product.price ? (
            <div className="mt-4 flex items-end gap-2">
              <div className="font-display text-2xl font-bold tracking-tight">₹{product.price.toLocaleString('en-IN')}</div>
              {product.original_price && (
                <div className="text-sm text-neutral-400 line-through mb-0.5">₹{product.original_price.toLocaleString('en-IN')}</div>
              )}
            </div>
          ) : (
            <div className="mt-4 text-sm text-neutral-500">
              {product.provider} · Cashback tracked automatically
            </div>
          )}

          <div className="mt-auto pt-5 flex items-center gap-2">
            <Button
              data-testid={`product-cta-${product.id}`}
              onClick={handleCta}
              className="flex-1 h-10 bg-black hover:bg-[#0055FF] text-white font-medium transition-colors"
            >
              {product.cta_label || (isBanking ? "Apply Now" : "Shop Now")}
              <ArrowUpRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <div className="mt-2">
            <ShareButton product={product} />
          </div>
        </div>
      </Card>
      <LeadDialog open={openLead} onOpenChange={setOpenLead} product={product} />
      <MobileCaptureDialog
        open={openMobile}
        onOpenChange={setOpenMobile}
        product={product}
        onCaptured={async () => {
          // Now that we have a HITCH-USER id, resume the original flow
          if (product.lead_mode === "redirect_only") {
            await doRedirect();
          } else {
            setOpenLead(true);
          }
        }}
      />
    </>
  );
}
